import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { toastWithLink } from '@/components/ui/toast';
import {
  activateOrderSource,
  deactivateOrderSource,
  orderSourcesQueryKeys,
} from '@api/orderSources';
import { getFieldErrors } from '@api/client';
import { useTranslation } from '../useTranslation';

/**
 * Status toggle for OrderSource. Hits the dedicated POST endpoints
 * (`/api/order_sources/:id/activate` and `/api/order_sources/:id/deactivate`)
 * — mirrors ResaleItem / Product. State transitions get their own routes
 * (greppable in logs, easier to add per-action authorization or side effects)
 * rather than going through the generic PATCH update path.
 */
export function useUpdateOrderSourceStatus() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      active ? activateOrderSource(id) : deactivateOrderSource(id),
    onSuccess: (orderSource, variables) => {
      queryClient.invalidateQueries({ queryKey: orderSourcesQueryKeys.lists(), refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: orderSourcesQueryKeys.detail(variables.id) });
      toastWithLink({
        name: orderSource.name,
        href: `/order_sources/${orderSource.id}/edit`,
        label: t(orderSource.active ? 'toast.activated' : 'toast.deactivated'),
      });
    },
    onError: (error) => {
      // Surface per-field 422 reasons (matches useUpdateOrderSource pattern).
      // ApiError.message resolves to the server's generic i18n string; field
      // errors carry the actionable per-field detail.
      const fieldErrors = getFieldErrors(error);
      const description = fieldErrors
        ? Object.values(fieldErrors).join(' • ')
        : t('toast.status_update_error');
      toast({ description, variant: 'destructive' });
    },
  });
}
